import { diff } from 'fast-myers-diff';

export const splitLines = (str) => str.split('\n');

export const compareCodigos = (code1, code2) => {
  const codigoOriginal = splitLines(code1);
  codigoOriginal.shift();
  const codigoModificado = splitLines(code2);
  codigoModificado.shift();

  const originalCode = { linhas: [] };
  const modifiedCode = { linhas: [] };

  const diffGen = diff(codigoOriginal, codigoModificado);
  let result = diffGen.next();
  let lastEx = 0, lastEy = 0;

  const pairedLines = [];

  while (!result.done) {
    const [sx, ex, sy, ey] = result.value;

    for (let i = lastEx; i < sx; i++) {
      originalCode.linhas.push({
        linha: codigoOriginal[i],
        numero: i + 1,
        tipo: 'context',
      });
    }
    for (let i = lastEy; i < sy; i++) {
      modifiedCode.linhas.push({
        linha: codigoModificado[i],
        numero: i + 1,
        tipo: 'context',
      });
    }

    const originalLines = [];
    const modifiedLines = [];

    if (sy === ey) {
      for (let i = sx; i < ex; i++) {
        const linhaOriginal = {
          linha: codigoOriginal[i],
          numero: i + 1,
          tipo: 'original',
        };
        originalLines.push(linhaOriginal);
        originalCode.linhas.push(linhaOriginal);
      }
      pairedLines.push({ original: originalLines, modified: [] });
    } else if (sx === ex) {
      for (let i = sy; i < ey; i++) {
        const linhaModificada = {
          linha: codigoModificado[i],
          numero: i + 1,
          tipo: 'modified',
        };
        modifiedLines.push(linhaModificada);
        modifiedCode.linhas.push(linhaModificada);
      }
      pairedLines.push({ original: [], modified: modifiedLines });
    } else {
      for (let i = sx; i < ex; i++) {
        const linhaOriginal = {
          linha: codigoOriginal[i],
          numero: i + 1,
          tipo: 'original',
        };
        originalLines.push(linhaOriginal);
        originalCode.linhas.push(linhaOriginal);
      }
      for (let i = sy; i < ey; i++) {
        const linhaModificada = {
          linha: codigoModificado[i],
          numero: i + 1,
          tipo: 'modified',
        };
        modifiedLines.push(linhaModificada);
        modifiedCode.linhas.push(linhaModificada);
      }
      pairedLines.push({ original: originalLines, modified: modifiedLines });
    }

    lastEx = ex;
    lastEy = ey;

    result = diffGen.next();
  }

  for (let i = lastEx; i < codigoOriginal.length; i++) {
    originalCode.linhas.push({
      linha: codigoOriginal[i],
      numero: i + 1,
      tipo: 'context',
    });
  }
  for (let i = lastEy; i < codigoModificado.length; i++) {
    modifiedCode.linhas.push({
      linha: codigoModificado[i],
      numero: i + 1,
      tipo: 'context',
    });
  }

  return { pl: pairedLines, original: originalCode, modified: modifiedCode };
};

export const determineFileName = (code, baseName) => {
  const extensionMap = {
    python: '.py',
    javascript: '.js',
    html: '.html',
    css: '.css',
    java: '.java',
    csharp: '.cs',
    c: '.c',
    cpp: '.cpp',
    ruby: '.rb',
    perl: '.pl',
    php: '.php',
    typescript: '.ts',
    swift: '.swift',
    kotlin: '.kt',
    scala: '.scala',
    go: '.go',
    r: '.r',
    matlab: '.m',
    bash: '.sh',
    powershell: '.ps1',
    sql: '.sql',
    xml: '.xml',
    json: '.json',
    yaml: '.yaml',
    markdown: '.md',
    dockerfile: 'Dockerfile',
    gitignore: '.gitignore',
    plaintext: '.txt',
  };

  const extension = code.linhas[0]?.linha
    ? extensionMap[code.linhas[0]?.linha.toLowerCase()] || '.txt'
    : '.txt';
  return `${baseName}${extension}`;
};

export const performReplacementUsingPair = (
  targetCode,
  fromLines,
  toLines
) => {
  if (!fromLines || !toLines || (fromLines.length === 0 && toLines.length === 0)) {
    return;
  }

  let firstAffectedLineNumber = (fromLines[0]?.numero ?? toLines[0]?.numero) || 1;

  if (firstAffectedLineNumber === undefined) {
    console.error('First affected line number is undefined');
    return;
  }

  let linesBefore = targetCode.linhas.slice(0, firstAffectedLineNumber - 1);
  let linesAfter = targetCode.linhas.slice(
    firstAffectedLineNumber - 1 + fromLines.length
  );

  let newLines = [];
  for (let i = 0; i < toLines.length; i++) {
    if (i < fromLines.length && fromLines[i].numero) {
      newLines.push({ ...toLines[i], numero: fromLines[i].numero });
    } else {
      newLines.push({ ...toLines[i], numero: firstAffectedLineNumber + i });
    }
  }

  let concatenatedLines = [...linesBefore, ...newLines, ...linesAfter];

  concatenatedLines.forEach((line, index) => {
    line.numero = index + 1;
  });

  targetCode.linhas = concatenatedLines;
};

export function escapeHTML(str) {
  if (!str) return '';
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export const highlightDifferences = (originalLine, modifiedLine) => {
  // Escapa os caracteres HTML primeiro
  const escapedOriginal = escapeHTML(originalLine);
  const escapedModified = escapeHTML(modifiedLine);

  const originalWords = escapedOriginal.split(' ');
  const modifiedWords = escapedModified.split(' ');
  const maxLength = Math.max(originalWords.length, modifiedWords.length);

  const highlightedOriginal = [];
  const highlightedModified = [];

  for (let i = 0; i < maxLength; i++) {
    if (originalWords[i] !== modifiedWords[i]) {
      // Envolve as palavras diferentes com spans de destaque
      highlightedOriginal.push(`<span class="highlight">${originalWords[i] || ''}</span>`);
      highlightedModified.push(`<span class="highlight">${modifiedWords[i] || ''}</span>`);
    } else {
      // Palavras iguais são adicionadas sem destaque
      highlightedOriginal.push(originalWords[i]);
      highlightedModified.push(modifiedWords[i]);
    }
  }

  return {
    highlightedOriginal: highlightedOriginal.join(' '),
    highlightedModified: highlightedModified.join(' ')
  };
};
